import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { useToast } from './ui/use-toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const AddSyndicatorModal = ({ dealId, dealFunding, dealPayback, onClose, onSuccess }) => {
  const { toast } = useToast();
  const [companies, setCompanies] = useState([]);
  const [formData, setFormData] = useState({
    company_id: '',
    percentage: '',
    commission_percent: '',
    is_main_lender: false
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const token = localStorage.getItem('mcaToken');
      const response = await axios.get(`${API_BASE}/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data || []);
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  // Calculate participated amount (what they put in) = Funding × Percentage
  const participatedAmount = formData.percentage && dealFunding 
    ? (dealFunding * parseFloat(formData.percentage) / 100).toFixed(2)
    : '0.00';
  
  // Calculate take home amount (what they get back) = Payback × Percentage
  const takeHomeAmount = formData.percentage && dealPayback
    ? (dealPayback * parseFloat(formData.percentage) / 100).toFixed(2)
    : '0.00';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.company_id || !formData.percentage) {
      toast({ title: 'Error', description: 'Please select a company and enter percentage', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('mcaToken');
      
      // Find selected company
      const selectedCompany = companies.find(c => c.email === formData.company_id);
      
      await axios.post(`${API_BASE}/deals/${dealId}/syndicators`, {
        company_id: formData.company_id,
        name: selectedCompany.company || selectedCompany.name,
        email: selectedCompany.email,
        percentage: parseFloat(formData.percentage),
        commission_percent: parseFloat(formData.commission_percent) || 0,
        is_main_lender: formData.is_main_lender
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({ title: 'Success', description: 'Syndicator added successfully' });
      onSuccess();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to add syndicator', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-8 max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold" data-testid="add-syndicator-modal-title">Add Syndicator</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="add-syndicator-modal-close">
            <X size={24} />
          </button>
        </div>

        {/* Deal Info Display */}
        <div className="bg-blue-50 p-4 rounded mb-4 grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600">Deal Funding Amount</p>
            <p className="text-lg font-bold text-gray-900">${dealFunding?.toLocaleString() || '0'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Deal Payback Amount</p>
            <p className="text-lg font-bold text-gray-900">${dealPayback?.toLocaleString() || '0'}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium mb-2">Select Company *</label>
            <select
              required
              data-testid="syndicator-company-select"
              value={formData.company_id}
              onChange={(e) => setFormData({...formData, company_id: e.target.value})}
              className="w-full border rounded p-2"
              disabled={loading}
            >
              <option value="">-- Select a company --</option>
              {companies.map((company, idx) => (
                <option key={idx} value={company.email}>
                  {company.company || company.name} ({company.email})
                </option>
              ))}
            </select>
            {companies.length === 0 && (
              <p className="text-xs text-orange-600 mt-1">
                No companies found. Please add companies in the "Outhouse Companies" page first.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Percentage *</label>
            <Input
              required
              type="number"
              step="0.01"
              min="0"
              max="100"
              data-testid="syndicator-percentage-input"
              value={formData.percentage}
              onChange={(e) => setFormData({...formData, percentage: e.target.value})}
              placeholder="50"
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-1">Enter the percentage of the deal this syndicator is participating in</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">ISO CM % (your cut from their profit)</label>
            <Input
              type="number"
              step="0.01"
              min="0"
              max="100"
              data-testid="syndicator-commission-input"
              value={formData.commission_percent}
              onChange={(e) => setFormData({...formData, commission_percent: e.target.value})}
              placeholder="10"
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-1">ISO CM deducted from syndicator's profit — paid off the rip</p>
          </div>

          {/* Auto-calculated amounts display */}
          {formData.percentage && parseFloat(formData.percentage) > 0 && (
            <div className="bg-green-50 p-4 rounded space-y-3" data-testid="syndicator-calculation-display">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Amount They're In (Funding x {formData.percentage}%)</p>
                  <p className="text-xl font-bold text-green-700" data-testid="participated-amount-display">${parseFloat(participatedAmount).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Their RTR (Payback x {formData.percentage}%)</p>
                  <p className="text-xl font-bold text-blue-700" data-testid="take-home-amount-display">${parseFloat(takeHomeAmount).toLocaleString()}</p>
                </div>
              </div>
              {formData.commission_percent && parseFloat(formData.commission_percent) > 0 && (
                <div className="border-t pt-3 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">ISO CM ({formData.commission_percent}% of RTR)</p>
                    <p className="text-lg font-bold text-orange-600">${(parseFloat(takeHomeAmount) * parseFloat(formData.commission_percent) / 100).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">They Actually Take Home</p>
                    <p className="text-lg font-bold text-green-700">${(parseFloat(takeHomeAmount) - (parseFloat(takeHomeAmount) * parseFloat(formData.commission_percent) / 100)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_main_lender"
              checked={formData.is_main_lender}
              onChange={(e) => setFormData({...formData, is_main_lender: e.target.checked})}
              className="w-4 h-4"
              data-testid="syndicator-main-lender-checkbox"
              disabled={loading}
            />
            <label htmlFor="is_main_lender" className="text-sm font-medium">Main Lender</label>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button type="submit" disabled={loading} className="flex-1 bg-green-600 hover:bg-green-700" data-testid="add-syndicator-submit-btn">
              {loading ? 'Adding...' : 'Add Syndicator'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} className="flex-1" disabled={loading} data-testid="add-syndicator-cancel-btn">
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddSyndicatorModal;
